import React, { useMemo } from 'react'
import { Dropdown } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import { Button } from '../../../../components/Button/Button'
import { type User } from '../../../../models/User'
import { useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { type IntegrationServiceId } from '../../../../common/integrations'
import { getCrossClientIntegrationDisplayName } from '../../integrationDisplay'
import { CrossClientIntegrationIcon } from '../../CrossClientIntegrationIcon'
import { getExternalLink } from '../../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/utils'
import { type SyncStatusView } from '../../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/models'
import { ReactComponent as ZocksIcon } from '../../../../assets/images/ZocksIcon-primary.svg'
import './CrossClientViewProfileButton.scss'

const EXTERNAL_CONTACT_ID_KEY: Record<string, string> = { precisefp: 'row_id' }
const DEFAULT_EXTERNAL_CONTACT_ID_KEY = 'id'

interface CrossClientProfileLink {
  serviceId: IntegrationServiceId
  label: string
  href: string
}

const getCrossClientIntegrationLinks = (client: User, currentUser: User | null): CrossClientProfileLink[] =>
  (client.externalInfos ?? []).reduce<CrossClientProfileLink[]>((links, info) => {
    const serviceId = info.serviceId as IntegrationServiceId
    const key = EXTERNAL_CONTACT_ID_KEY[info.serviceId] ?? DEFAULT_EXTERNAL_CONTACT_ID_KEY
    const externalContactId = info.contactValues?.[key]
    if (!externalContactId) {
      return links
    }
    const syncStatus: SyncStatusView = {
      type: 'contact',
      serviceId,
      syncedAt: '',
      externalObjectName: 'contact',
      externalId: String(externalContactId),
      externalContactId: String(externalContactId),
    }
    const href = getExternalLink({ syncStatus, currentUser })
    if (!href) {
      return links
    }
    links.push({ serviceId, label: getCrossClientIntegrationDisplayName(serviceId), href })
    return links
  }, [])

export interface CrossClientViewProfileButtonProps {
  client: User
  className?: string
  label: string
}

const ViewProfileLinkToggle = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(({ className, children, ...rest }, ref) => (
  <Button ref={ref} variant='primaryLinkButton' className={classNames('p-0 border-0', className)} {...rest}>
    {children}
  </Button>
))
ViewProfileLinkToggle.displayName = 'ViewProfileLinkToggle'

interface ProfileLinkItemProps {
  href: string
  label: string
  // An app route goes through the router, so it gets the app's base path; an integration's is a full URL.
  isAppRoute?: boolean
  children: React.ReactNode
}

const ProfileLinkItem: React.FC<ProfileLinkItemProps> = ({ href, label, isAppRoute = false, children }) => (
  <Dropdown.Item
    {...(isAppRoute ? { as: Link as React.ElementType, to: href } : { href })}
    target='_blank'
    rel='noopener noreferrer'
    className='d-flex align-items-center justify-content-between gap-4 rounded-2 px-2 py-2'
  >
    <span className='d-flex align-items-center gap-2'>
      {children}
      <span className='paragraph-medium zocks-text-gray-900'>Open in {label}</span>
    </span>
    <i className='bi bi-arrow-up-right zocks-text-gray-900' />
  </Dropdown.Item>
)

export const CrossClientViewProfileButton: React.FC<CrossClientViewProfileButtonProps> = ({ client, className, label }) => {
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const integrationLinks = useMemo(() => getCrossClientIntegrationLinks(client, currentUser), [client, currentUser])

  return (
    <Dropdown>
      <Dropdown.Toggle as={ViewProfileLinkToggle} bsPrefix='cross-client-view-profile' className={className}>
        {label}
      </Dropdown.Toggle>
      <Dropdown.Menu className='cross-client-view-profile-menu p-1'>
        <ProfileLinkItem href={`/library/manage-contacts/contacts/${client.id}`} label='Zocks' isAppRoute>
          <ZocksIcon width={16} height={16} />
        </ProfileLinkItem>
        {integrationLinks.map((link) => (
          <ProfileLinkItem key={link.serviceId} href={link.href} label={link.label}>
            <CrossClientIntegrationIcon serviceId={link.serviceId} width={16} height={16} />
          </ProfileLinkItem>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  )
}
